/* Elwardany header: predictive search + mega menu states. No dependencies. */
(() => {
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  class ElwSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.elw-search__input');
      this.panel = this.querySelector('.elw-search__panel');
      if (!this.input || !this.panel) return;
      this.cache = new Map();
      this.active = -1;
      this.timer = null;
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => { if (this.panel.innerHTML && this.input.value.trim()) this.open(); });
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
    }
    onInput() {
      clearTimeout(this.timer);
      const q = this.input.value.trim();
      if (q.length < 2) { this.close(); return; }
      this.timer = setTimeout(() => this.search(q), 220);
    }
    async search(q) {
      const d = this.dataset;
      if (!this.cache.has(q)) {
        this.renderStatus(d.tSearching);
        // Storefront search (supports Arabic) rendered through the Section Rendering API.
        const params = new URLSearchParams({ q, type: 'product', 'options[prefix]': 'last', section_id: 'elw-search-suggest' });
        try {
          const res = await fetch(`${d.searchUrl}?${params}`, { headers: { Accept: 'text/html' } });
          if (!res.ok) throw new Error(res.status);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const frag = doc.querySelector('.elw-suggest');
          this.cache.set(q, frag ? frag.innerHTML : '');
        } catch (e) { this.close(); return; }
      }
      if (this.input.value.trim() !== q) return;
      const html = this.cache.get(q);
      if (!html) { this.close(); return; }
      this.panel.innerHTML = html;
      this.active = -1;
      this.open();
    }
    renderStatus(text) { this.panel.innerHTML = `<p class="elw-search__status">${esc(text)}</p>`; this.open(); }
    options() { return [...this.panel.querySelectorAll('[role="option"], .elw-search__all')]; }
    onKey(e) {
      if (e.key === 'Escape') { this.close(); return; }
      if (this.panel.hidden || !['ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault();
      const opts = this.options();
      if (!opts.length) return;
      this.active = (this.active + (e.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length;
      opts.forEach((o, i) => o.classList.toggle('is-active', i === this.active));
      opts[this.active].focus();
    }
    open() { this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true'); this.classList.add('is-open'); }
    close() { this.panel.hidden = true; this.input.setAttribute('aria-expanded', 'false'); this.classList.remove('is-open'); }
  }
  if (!customElements.get('elw-search')) customElements.define('elw-search', ElwSearch);

  // Mega menu: hover intent on pointer devices, keyboard focus, Escape to close.
  const initNav = () => {
    document.querySelectorAll('.elw-nav__item--mega').forEach((item) => {
      const link = item.querySelector('.elw-nav__link');
      let t;
      const set = (open) => { item.classList.toggle('is-open', open); link.setAttribute('aria-expanded', String(open)); };
      item.addEventListener('mouseenter', () => { clearTimeout(t); t = setTimeout(() => set(true), 90); });
      item.addEventListener('mouseleave', () => { clearTimeout(t); t = setTimeout(() => set(false), 120); });
      item.addEventListener('focusin', () => set(true));
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) set(false); });
      item.addEventListener('keydown', (e) => { if (e.key === 'Escape') { set(false); link.focus(); } });
    });
  };
  // Expose header height for sticky offsets (filters bar, product sticky etc.).
  const setHeight = () => {
    const h = document.querySelector('.elw-header-section');
    if (h) document.documentElement.style.setProperty('--elw-header-h', `${Math.round(h.getBoundingClientRect().height)}px`);
  };
  // One search engine: any [data-elw-search-trigger] (bottom mobile nav) opens the header search.
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-elw-search-trigger]');
    if (!t) return;
    const input = [...document.querySelectorAll('.elw-search__input')].find((i) => i.offsetParent !== null);
    if (!input) return; // no header search visible → normal link to the search page
    e.preventDefault();
    input.focus({ preventScroll: true });
    input.select();
    const box = input.closest('elw-search');
    if (input.value.trim().length >= 2 && box && box.panel && box.panel.innerHTML) box.open();
  });
  // DEV-010: while any modal / drawer / dialog is open (quick add, cart, menu, filters), the bottom
  // navigation steps aside so it can never cover purchase controls.
  const overlaySel = 'hdt-modal[open], hdt-drawer[open], hdt-dialog[open], hdt-lazy-modal[open], dialog[open], hdt-cart-openable[open]';
  const syncOverlay = () => document.body.classList.toggle('elw-overlay-open', !!document.querySelector(overlaySel));
  new MutationObserver(syncOverlay).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
  const ready = () => { initNav(); setHeight(); syncOverlay(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true }); else ready();
  window.addEventListener('resize', setHeight, { passive: true });
})();
